import React, { useState, useEffect } from 'react';
import { Question } from '../../../data/types';
import { GameIcons } from '../../styles/GameIcons';
import '../../styles/GameStyleUI.css';
import { motion, AnimatePresence } from 'framer-motion';

interface LearningPhaseQuizProps {
  questions: Question[];
  onComplete: (score: { correct: number; total: number }) => void;
}

const LearningPhaseQuiz: React.FC<LearningPhaseQuizProps> = ({ questions, onComplete }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [isAnswerCorrect, setIsAnswerCorrect] = useState<boolean | null>(null);
  const [showHint, setShowHint] = useState(false);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [answerState, setAnswerState] = useState<'unanswered' | 'correct' | 'incorrect'>('unanswered');
  const [confetti, setConfetti] = useState(false);
  const [shakingWrong, setShakingWrong] = useState(false);

  // Reset state when moving to a new question
  useEffect(() => {
    setSelectedAnswer(null);
    setIsAnswerCorrect(null);
    setShowHint(false);
    setAnswerState('unanswered');
  }, [currentQuestionIndex]);

  const handleAnswerSelect = (answer: string) => {
    if (answerState !== 'unanswered') return; // Prevent changing after submitting
    setSelectedAnswer(answer);
  };

  const handleSubmitAnswer = () => {
    if (!selectedAnswer || answerState !== 'unanswered') return;
    
    const currentQuestion = questions[currentQuestionIndex];
    const correct = selectedAnswer === currentQuestion.correctAnswer;
    
    setIsAnswerCorrect(correct);
    
    if (correct) {
      setScore(prev => ({ ...prev, correct: prev.correct + 1 }));
      setAnswerState('correct');
      setConfetti(true);
      
      // Clear confetti effect after animation
      setTimeout(() => {
        setConfetti(false);
      }, 2000);
      
      // Move to next question after delay
      setTimeout(() => {
        if (currentQuestionIndex < questions.length - 1) {
          setCurrentQuestionIndex(prev => prev + 1);
        } else {
          // Quiz completed
          onComplete({ 
            correct: score.correct + 1, 
            total: questions.length 
          });
        }
      }, 1500);
    } else {
      setAnswerState('incorrect');
      setShakingWrong(true);
      
      // Stop shaking effect
      setTimeout(() => {
        setShakingWrong(false);
      }, 500);
    }
  };

  const handleShowHint = () => {
    setShowHint(true);
  };

  const handleSkipToNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setScore(prev => ({ ...prev, total: prev.total + 1 }));
    } else {
      // Quiz completed
      onComplete({ 
        correct: score.correct, 
        total: score.total + 1 
      });
    }
  };

  // If no questions available
  if (questions.length === 0) {
    return (
      <div className="p-8 text-center">
        <GameIcons.Question size={64} className="mx-auto mb-4" color="#6366F1" />
        <h3 className="text-2xl font-bold text-gray-800 mb-2">
          No Questions Available
        </h3>
        <p className="text-gray-600 mb-6">
          There are no practice questions available for this standard.
        </p>
        <button
          onClick={() => onComplete({ correct: 0, total: 0 })}
          className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          Continue to Next Step
        </button>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div className="bg-gradient-to-b from-purple-50 to-indigo-50 p-6 rounded-xl relative">
      {/* Confetti effect overlay */}
      {confetti && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
          {Array.from({ length: 50 }).map((_, i) => (
            <motion.div 
              key={i}
              initial={{ 
                y: -20, 
                x: Math.random() * 100 - 50 + (window.innerWidth / 2), 
                opacity: 1, 
                scale: Math.random() * 0.5 + 0.5 
              }}
              animate={{ 
                y: window.innerHeight, 
                x: Math.random() * window.innerWidth, 
                opacity: 0, 
                rotate: Math.random() * 360 
              }}
              transition={{ duration: Math.random() * 1.5 + 0.5, ease: "easeOut" }}
              className="absolute w-3 h-3"
              style={{ 
                backgroundColor: ['#FF5E5B', '#4F86C6', '#FFCB47', '#44BBA4', '#FF9505'][Math.floor(Math.random() * 5)],
                borderRadius: Math.random() > 0.5 ? '50%' : '0%'
              }}
            />
          ))}
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600 flex items-center">
          <GameIcons.Question className="mr-2" size={24} color="#6366F1" />
          <span>Practice Time</span>
        </h2>
        <div className="flex items-center px-4 py-1 bg-indigo-100 rounded-full">
          <span className="text-indigo-700 font-medium">
            Question {currentQuestionIndex + 1} of {questions.length}
          </span>
        </div>
      </div>

      {/* Question card */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="mb-6">
          <span className="inline-block px-3 py-1 bg-indigo-100 text-indigo-800 text-sm font-medium rounded-full mb-2">
            {currentQuestion.difficulty} Question
          </span>
          <h3 className="text-xl font-bold text-gray-800">
            {currentQuestion.text}
          </h3>
        </div>

        {/* Answer options */}
        <div className="space-y-3 mb-6">
          {currentQuestion.options?.map((option, index) => {
            // Handle both string and StringOption types
            const optionText = typeof option === 'string' ? option : option.text;
            const isSelected = selectedAnswer === optionText;
            const isCorrect = optionText === currentQuestion.correctAnswer;
            
            // For word problems without multiple choice options, show input field
            if (!currentQuestion.options || currentQuestion.options.length === 0) {
              return (
                <div key="input-answer" className="mt-4">
                  <label htmlFor="answer-input" className="block text-sm font-medium text-gray-700 mb-1">Your Answer:</label>
                  <input
                    id="answer-input"
                    type="text"
                    value={selectedAnswer || ''}
                    onChange={(e) => setSelectedAnswer(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Type your answer here..."
                  />
                </div>
              );
            }
            
            return (
              <div
                key={index}
                className={`p-3 border rounded-lg cursor-pointer transition-all ${
                  isSelected ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-200'
                }`}
                onClick={() => handleAnswerSelect(optionText)}
              >
                <div className="flex items-center">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${
                    isSelected ? 'bg-purple-500 text-white' : 'bg-gray-200 text-gray-700'
                  }`}>
                    {String.fromCharCode(65 + index)}
                  </div>
                  <span className="text-gray-800 flex items-center">
                    {typeof option !== 'string' && option.image && (
                      <img src={option.image} alt="Option visual" className="w-8 h-8 object-contain mr-2 inline-block" />
                    )}
                    {optionText}
                  </span>
                </div>
              </div>
            );
          })}
          
          {/* For word problems without options, show text input */}
          {(!currentQuestion.options || currentQuestion.options.length === 0) && (
            <div className="mt-4">
              <label htmlFor="answer-input" className="block text-sm font-medium text-gray-700 mb-1">Your Answer:</label>
              <input
                id="answer-input"
                type="text"
                value={selectedAnswer || ''}
                onChange={(e) => setSelectedAnswer(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="Type your answer here..."
              />
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex justify-between items-center">
          <div className="flex space-x-3">
            {answerState === 'incorrect' && !showHint && (
              <button 
                onClick={handleShowHint}
                className="px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors flex items-center"
              >
                <GameIcons.LightBulb size={18} className="mr-1" color="currentColor" />
                <span>Show Hint</span>
              </button>
            )}
            
            {answerState === 'incorrect' && (
              <button 
                onClick={handleSkipToNextQuestion}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center"
              >
                <GameIcons.ArrowRight size={18} className="mr-1" color="currentColor" />
                <span>Skip Question</span>
              </button>
            )}
          </div>
          
          {/* Always show submit button for unanswered questions */}
          {answerState === 'unanswered' && (
            <button
              onClick={handleSubmitAnswer}
              disabled={!selectedAnswer}
              className={`px-6 py-3 rounded-lg transition-colors flex items-center ${
                selectedAnswer 
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-md' 
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <span>Submit Answer</span>
              <GameIcons.Check size={18} className="ml-2" color="currentColor" />
            </button>
          )}
          
          {/* Show next question button for correct answers */}
          {answerState === 'correct' && currentQuestionIndex < questions.length - 1 && (
            <button
              onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
              className="px-6 py-3 rounded-lg transition-colors flex items-center bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white shadow-md"
            >
              <span>Next Question</span>
              <GameIcons.ArrowRight size={18} className="ml-2" color="currentColor" />
            </button>
          )}
        </div>
      </div>
      
      {/* Feedback messages */}
      <AnimatePresence>
        {showHint && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4"
          >
            <div className="flex items-start">
              <GameIcons.LightBulb size={20} className="mr-2 mt-1 flex-shrink-0" color="#B45309" />
              <div>
                <h4 className="font-bold text-amber-800 mb-1">Hint:</h4>
                <p className="text-amber-700">
                  {currentQuestion.hints?.[0] || "Think about the properties we discussed in the video. Look carefully at all options before deciding."}
                </p>
              </div>
            </div>
          </motion.div>
        )}
        
        {isAnswerCorrect === true && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-4 bg-green-50 border border-green-200 rounded-lg"
          >
            <div className="flex items-center">
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-3">
                <GameIcons.Trophy size={24} color="#FFFFFF" />
              </div>
              <div>
                <h4 className="font-bold text-green-800 text-lg">Excellent!</h4>
                <p className="text-green-700">
                  That's the correct answer! Great job understanding this concept.
                </p>
              </div>
            </div>
          </motion.div>
        )}
        
        {isAnswerCorrect === false && !showHint && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-4 bg-red-50 border border-red-200 rounded-lg"
          >
            <div className="flex items-center">
              <GameIcons.AlertTriangle size={24} className="mr-3" color="#B91C1C" />
              <div>
                <h4 className="font-bold text-red-800">Not quite right</h4>
                <p className="text-red-700">
                  That's not the correct answer. Try again or use a hint!
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Progress tracker */}
      <div className="mt-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600">Progress</span>
          <span className="text-sm font-medium text-indigo-700">
            {Math.round((currentQuestionIndex / questions.length) * 100)}%
          </span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-indigo-500 to-purple-500"
            style={{ width: `${(currentQuestionIndex / questions.length) * 100}%` }}
          ></div>
        </div>
      </div>
      
      {/* Auto-save indicator */}
      <div className="mt-4 text-center">
        <span className="inline-flex items-center text-xs text-green-600">
          <GameIcons.CloudCheck size={14} className="mr-1" color="currentColor" />
          Your progress is being saved automatically
        </span>
      </div>
    </div>
  );
};

export default LearningPhaseQuiz;
